import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  Icon,
  InputText,
  ListScreen,
  RigoFilters,
  Select,
  Tooltip,
  type ColumnDef,
  type SelectOption,
} from "@/deps/ui-library";
import { useParties, usePartyDetails, useSaveParty, useWithholdingCodes } from "@/api/queries";
import type { Id, Party, PartyForm } from "@/api/types";
import { Dialog } from "@/components/Dialog";
import { PermittedAction } from "@/components/PermittedAction";
import { useCan } from "@/app/session";
import { PAYMENT_TERMS } from "@/lib/paymentTerms";
import { useTaxWords } from "@/app/session";

const WORDS = {
  customer: { title: "Customers", one: "customer", icon: "Users" },
  supplier: { title: "Suppliers", one: "supplier", icon: "Building2" },
  employee: { title: "Employees", one: "employee", icon: "Users" },
} as const;

/**
 * Who a company sells to or buys from. A document cannot be entered without one, so this is the
 * first thing a new company needs after its books are set up.
 */
export function PartiesPage({ kind }: { kind: "customer" | "supplier" }) {
  const say = useTaxWords();
  const words = WORDS[kind];
  const can = useCan();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data: parties, isPending, error } = useParties(kind, search);
  const [editing, setEditing] = useState<Id | "new" | null>(null);

  const manages = can("settings.parties.manage");
  const columns: Array<ColumnDef<Party>> = [
    {
      id: "name",
      header: "Name",
      cell: ({ row: { original: party } }) =>
        kind === "customer" ? (
          <Link
            to="/sales/customers/$id"
            params={{ id: party.id }}
            className="text-foreground text-sm hover:underline"
          >
            {party.name}
          </Link>
        ) : (
          <Link
            to="/purchases/suppliers/$id"
            params={{ id: party.id }}
            className="text-foreground text-sm hover:underline"
          >
            {party.name}
          </Link>
        ),
    },
    {
      id: "pan",
      header: say("PAN"),
      cell: ({ row: { original: party } }) => party.pan ?? "",
    },
    ...(manages
      ? [
          {
            id: "actions",
            header: "",
            meta: { textAlign: "right" as const },
            cell: ({ row: { original: party } }: { row: { original: Party } }) => (
              <Tooltip content={`Edit ${words.one}`}>
                <Button.IconButton
                  icon="Pencil"
                  iconVariant="cell"
                  variant="secondary"
                  size="xs"
                  aria-label={`Edit ${party.name}`}
                  onClick={() => setEditing(party.id)}
                />
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <ListScreen
        // The shell's main has the padding.
        className="p-0"
        title={words.title}
        actions={
          <>
            <PermittedAction
              permission="reports.view"
              onClick={() =>
                void navigate({
                  to:
                    kind === "customer"
                      ? "/sales/receivable-analysis"
                      : "/purchases/payable-analysis",
                })
              }
            >
              {kind === "customer" ? "Receivable analysis" : "Payable analysis"}
            </PermittedAction>
            <PermittedAction
              permission="settings.parties.manage"
              leftIcon="Plus"
              onClick={() => setEditing("new")}
            >
              New {words.one}
            </PermittedAction>
          </>
        }
        filters={
          <RigoFilters.Root>
            <RigoFilters.StaticRow>
              <div className="w-60">
                <InputText.FilterSearch
                  aria-label={`Search ${words.one}s`}
                  placeholder={say("Search by name or PAN")}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onClear={() => setSearch("")}
                />
              </div>
            </RigoFilters.StaticRow>
          </RigoFilters.Root>
        }
        loading={isPending}
        error={error}
        empty={(parties?.length ?? 0) === 0}
        filtered={search !== ""}
        onClearFilters={() => setSearch("")}
        emptyState={{
          title: `No ${words.one}s yet`,
          description: `Add a ${words.one} to enter your first document for them.`,
          icon: <Icon name={words.icon} size="lg" color="inherit" />,
        }}
        filteredEmptyState={{
          title: `No ${words.one}s match`,
          description: say("Try another name or PAN."),
        }}
      >
        <DataTable
          aria-label={words.title}
          size="sm"
          enableSorting={false}
          columns={columns}
          data={parties ?? []}
          getRowId={(party) => party.id}
          pagination={{ mode: "manual", show: false }}
        />
      </ListScreen>

      {editing === "new" ? (
        <PartyDialog kind={kind} onClose={() => setEditing(null)} />
      ) : editing !== null ? (
        <EditParty id={editing} kind={kind} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

function EditParty({
  id,
  kind,
  onClose,
}: {
  id: Id;
  kind: "customer" | "supplier";
  onClose: () => void;
}) {
  const { data } = usePartyDetails(id);
  return data ? <PartyDialog kind={kind} id={id} initial={data} onClose={onClose} /> : null;
}

const EMPTY: Omit<PartyForm, "kind"> = {
  legalName: "",
  tradingName: "",
  pan: "",
  vatRegistered: false,
  legalRegistration: "",
  street: "",
  city: "",
  phone: "",
  email: "",
  creditLimit: "",
  paymentTermsDays: "",
  withholdingCode: "",
};

export function PartyDialog({
  kind,
  id,
  initial,
  onClose,
}: {
  kind: "customer" | "supplier" | "employee";
  id?: Id;
  initial?: PartyForm;
  onClose: () => void;
}) {
  const say = useTaxWords();
  const words = WORDS[kind];
  const [form, setForm] = useState<PartyForm>(initial ?? { ...EMPTY, kind });
  const save = useSaveParty();
  const { data: codes } = useWithholdingCodes();
  const field = (
    key:
      | "legalName"
      | "tradingName"
      | "pan"
      | "legalRegistration"
      | "street"
      | "city"
      | "phone"
      | "email",
  ) => ({
    value: form[key],
    onChange: (event: { target: { value: string } }) =>
      setForm({ ...form, [key]: event.target.value }),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={id ? `Edit ${words.one}` : `New ${words.one}`}
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={
            form.legalName.trim() === "" ||
            save.isPending ||
            !/^(\d+(\.\d{1,2})?)?$/.test(form.creditLimit.trim())
          }
          onClick={() => save.mutate({ id, form }, { onSuccess: onClose })}
        >
          {save.isPending ? "Saving" : `Save ${words.one}`}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(save.error as Error).message}
          />
        ) : null}
        <InputText label="Legal name" required {...field("legalName")} />
        <InputText label="Trading name" {...field("tradingName")} />
        <InputText label={say("PAN")} {...field("pan")} />
        {form.kind === "employee" ? null : (
          <InputText label="Company registration" {...field("legalRegistration")} />
        )}
        {kind === "employee" ? null : (
          <Checkbox
            checked={form.vatRegistered}
            onCheckedChange={(details) =>
              setForm({ ...form, vatRegistered: details.checked === true })
            }
            label={say("Registered for VAT")}
          />
        )}
        <InputText label="Street" {...field("street")} />
        <InputText label="City" {...field("city")} />
        <InputText label="Phone" {...field("phone")} />
        <InputText label="Email" type="email" {...field("email")} />
        {kind === "employee" ? null : (
          <Select
            label="Payment terms"
            options={PAYMENT_TERMS}
            value={form.paymentTermsDays}
            onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
              const option = Array.isArray(details.value) ? details.value[0] : details.value;
              setForm({ ...form, paymentTermsDays: option ? String(option.value) : "" });
            }}
            showClearButton={false}
          />
        )}
        {kind === "supplier" ? (
          <Select
            label="Withholding category"
            options={[
              { value: "", label: "No withholding" },
              ...(codes ?? []).map((code) => ({
                value: code.code,
                label: code.ratePct ? `${code.name} · ${code.ratePct}%` : code.name,
              })),
            ]}
            value={form.withholdingCode}
            onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
              const option = Array.isArray(details.value) ? details.value[0] : details.value;
              setForm({ ...form, withholdingCode: option ? String(option.value) : "" });
            }}
            showClearButton={false}
          />
        ) : null}
        {kind === "customer" ? (
          <InputText
            label="Credit limit"
            inputMode="decimal"
            placeholder="No limit"
            value={form.creditLimit}
            onChange={(event) => setForm({ ...form, creditLimit: event.target.value })}
          />
        ) : null}
      </div>
    </Dialog>
  );
}
